import { Delete, Edit } from '@mui/icons-material';
import { Box, IconButton, useMediaQuery } from '@mui/material';
import { useMemo, FC, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useQuery } from 'react-query';
import { Loader, Pagination, Table, MobileTable } from '../../../../components';
import { formatDate } from '../../../../helpers';
import { ISortDirection, INews, IResponse, IDateRange, IDomainUser } from '../../../../models';
import { AddEditNewsItemModal } from './news-item-modal';
import { getNews, deleteNewsItem } from '../../../../fetch';
import { NewsFilters } from './news-filters';
import { startOfDay } from 'date-fns';
import { endOfDay } from 'date-fns/esm';

interface INewsManagementList {
  showModal: (val) => void;
  isShowingModal: boolean;
}

export const NewsManagementList: FC<INewsManagementList> = ({ showModal, isShowingModal }) => {
  const isMobile = useMediaQuery('(max-width: 960px)');
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const [recordCount, setRecordCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [selectedSort, setSelectedSort] = useState<string>('CreatedDate');
  const [sortDirection, setSortDirection] = useState<{
    Category?: ISortDirection;
    Subject?: ISortDirection;
    Message?: ISortDirection;
    CreatedDate?: ISortDirection;
    DisplayDate?: ISortDirection;
    TakedownDate?: ISortDirection;
  }>({
    CreatedDate: 'Desc'
  });
  const [filters, setFilters] = useState<{
    subjectSearch?: string;
    messageSearch?: string;
    category?: string;
    vendorId?: string | number;
    displayDateStartDate?: string;
    displayDateEndDate?: string;
    takeDownDateStartDate?: string;
    takeDownDateEndDate?: string;
    createdDateStartDate?: string;
    createdDateEndDate?: string;
    username?: string | null;
  } | null>({});
  const [isDeleting, setDeleting] = useState(false);
  const [searchSubjectValue, setSearchSubjectValue] = useState<string>('');
  const [searchMessageValue, setSearchMessageValue] = useState<string>('');
  const [currentNewsItem, setCurrentNewsItem] = useState<INews | null>(null);
  const [vendorId, setVendorId] = useState<string | number>('');
  const [selectedCategory, setCategory] = useState<string>('');
  const [selectedEmployee, setSelectedEmployee] = useState<IDomainUser | null>(null);

  const [selectedDisplayDateRange, setSelectedDisplayDateRange] = useState<IDateRange>({
    startDate: null,
    endDate: null,
    key: 'selection',
    inputValue: ''
  });
  const [selectedTakedownDateRange, setSelectedTakedownDateRange] = useState<IDateRange>({
    startDate: null,
    endDate: null,
    key: 'selection',
    inputValue: ''
  });
  const [selectedCreatedDateRange, setSelectedCreatedDateRange] = useState<IDateRange>({
    startDate: null,
    endDate: null,
    key: 'selection',
    inputValue: ''
  });

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const {
    isLoading: isLoadingNews,
    data: newsData,
    refetch: refetchNews
  } = useQuery<IResponse<INews[]>, Error>(
    ['getNews', page, perPage, selectedSort, sortDirection, filters],
    () =>
      getNews({
        page: page + 1,
        perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        ...filters
      }),
    {
      onSuccess: data => {
        setRecordCount(data.totalRecordCount);
      },
      notifyOnChangeProps: 'tracked'
    }
  );
  const news = useMemo(() => newsData?.records || [], [newsData]);
  const handleEdit = async (newsItem: INews) => {
    showModal(true);
    setCurrentNewsItem(newsItem);
  };
  const handleDelete = async (original: INews) => {
    try {
      const result = window.confirm('Are you sure you want to delete this news item?');
      if (result) {
        setDeleting(true);
        await deleteNewsItem(original.newsItemId);
        await refetchNews();
        enqueueSnackbar(`News Item Deleted!`, {
          variant: 'success'
        });
      }
    } catch (error) {
      enqueueSnackbar(`Error deleting news item, please try again.`, {
        variant: 'error'
      });
    } finally {
      setDeleting(false);
    }
  };
  const columns = useMemo(() => {
    return [
      {
        Header: 'Date Created',
        accessor: 'dateCreated',
        isServerSorted: selectedSort === 'CreatedDate',
        isServerSortedDesc: sortDirection.CreatedDate === 'Desc',
        handleClickColumn: () => handleClickColumn('CreatedDate'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: INews } };
        }) => {
          return <>{original.dateCreated ? formatDate(original.dateCreated) : ''}</>;
        }
      },
      {
        Header: 'Category',
        accessor: 'category',
        isServerSorted: selectedSort === 'Category',
        isServerSortedDesc: sortDirection.Category === 'Desc',
        handleClickColumn: () => handleClickColumn('Category')
      },
      {
        Header: 'Subject',
        accessor: 'subject',
        overrideWidth: 300,
        isServerSorted: selectedSort === 'Subject',
        isServerSortedDesc: sortDirection.Subject === 'Desc',
        handleClickColumn: () => handleClickColumn('Subject')
      },
      {
        Header: 'Targeted Vendors',
        accessor: 'vendors',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: INews } };
        }) => {
          return <>{original.allVendors ? 'All' : original.targetedVendors}</>;
        }
      },
      {
        Header: 'Targeted Employees',
        accessor: 'usernames',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: INews } };
        }) => {
          return <>{original.allEmployees ? 'All' : original.targetedEmployees}</>;
        }
      },
      {
        Header: 'Targeted Homeowners',
        accessor: 'homeowners',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: INews } };
        }) => {
          return <>{original.allHomeowners ? 'All' : original.targetedHomeowners}</>;
        }
      },
      {
        Header: 'Targeted Sales Agents',
        accessor: 'salesAgents',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: INews } };
        }) => {
          return <>{original.allSales ? 'All' : original.targetedSales}</>;
        }
      },
      {
        Header: 'Display Date',
        accessor: 'displayDate',
        isServerSorted: selectedSort === 'DisplayDate',
        isServerSortedDesc: sortDirection.DisplayDate === 'Desc',
        handleClickColumn: () => handleClickColumn('DisplayDate'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: INews } };
        }) => {
          return <>{original.displayDate ? formatDate(original.displayDate) : ''}</>;
        }
      },
      {
        Header: 'Takedown Date',
        accessor: 'takeDownDate',
        isServerSorted: selectedSort === 'TakedownDate',
        isServerSortedDesc: sortDirection.TakedownDate === 'Desc',
        handleClickColumn: () => handleClickColumn('TakedownDate'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: INews } };
        }) => {
          return <>{original.takeDownDate ? formatDate(original.takeDownDate) : ''}</>;
        }
      },

      {
        id: 'actions',
        Header: '',
        accessor: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: INews } };
        }) => {
          return (
            <Box display="flex">
              <IconButton
                color="primary"
                title="Edit News Item"
                onClick={() => handleEdit(original)}
              >
                <Edit />
              </IconButton>
              <IconButton
                color="error"
                title="Delete News Item"
                onClick={() => handleDelete(original)}
              >
                <Delete />
              </IconButton>
            </Box>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [news, selectedSort, isDeleting, setDeleting, sortDirection]);

  return (
    <>
      {isDeleting && <Loader type="fullscreen" position="centered" />}
      <NewsFilters
        isLoading={isLoadingNews}
        selectedVendorId={vendorId}
        setSelectedVendorId={setVendorId}
        selectedUser={selectedEmployee}
        setSelectedUser={setSelectedEmployee}
        selectedDisplayDateRange={selectedDisplayDateRange}
        setSelectedDisplayDateRange={setSelectedDisplayDateRange}
        selectedTakedownDateRange={selectedTakedownDateRange}
        setSelectedTakedownDateRange={setSelectedTakedownDateRange}
        selectedCreatedDateRange={selectedCreatedDateRange}
        setSelectedCreatedDateRange={setSelectedCreatedDateRange}
        selectedCategory={selectedCategory}
        setSelectedCategory={setCategory}
        setHasAppliedFilters={setHasAppliedFilters}
        hasAppliedFilters={hasAppliedFilters}
        searchSubjectValue={searchSubjectValue}
        searchMessageValue={searchMessageValue}
        setSearchSubjectValue={setSearchSubjectValue}
        setSearchMessageValue={setSearchMessageValue}
        handleSubjectSearch={(val: string) => {
          setPage(0);
          setHasAppliedFilters(false);
          setFilters({
            ...filters,
            subjectSearch: val
          });
        }}
        handleMessageSearch={(val: string) => {
          setPage(0);
          setHasAppliedFilters(true);
          setFilters({
            ...filters,
            messageSearch: val
          });
        }}
        applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
          if (clearFilters) {
            setFilters({
              messageSearch: '',
              subjectSearch: '',
              category: '',
              vendorId: '',
              displayDateStartDate: '',
              displayDateEndDate: '',
              takeDownDateStartDate: '',
              takeDownDateEndDate: '',
              createdDateStartDate: '',
              createdDateEndDate: '',
              username: null
            });
          } else {
            setPage(0);
            setFilters({
              ...filters,
              messageSearch: searchMessageValue || '',
              subjectSearch: searchSubjectValue || '',
              category: selectedCategory || '',
              vendorId: vendorId || '',
              displayDateStartDate: selectedDisplayDateRange?.startDate
                ? startOfDay(selectedDisplayDateRange?.startDate).toISOString()
                : '',
              displayDateEndDate: selectedDisplayDateRange?.endDate
                ? endOfDay(selectedDisplayDateRange?.endDate).toISOString()
                : '',
              takeDownDateStartDate: selectedTakedownDateRange?.startDate
                ? startOfDay(selectedTakedownDateRange?.startDate).toISOString()
                : '',
              takeDownDateEndDate: selectedTakedownDateRange?.endDate
                ? endOfDay(selectedTakedownDateRange?.endDate).toISOString()
                : '',
              createdDateStartDate: selectedCreatedDateRange?.startDate
                ? startOfDay(selectedCreatedDateRange?.startDate).toISOString()
                : '',
              createdDateEndDate: selectedCreatedDateRange?.endDate
                ? endOfDay(selectedCreatedDateRange?.endDate).toISOString()
                : '',
              username: selectedEmployee?.accountName ?? null
            });
          }
        }}
      />
      <Box marginTop="1rem">
        <Table
          columns={columns}
          data={news}
          isLoading={isLoadingNews}
          hidePagination
          mobileBreakPoint={1100}
          ResponsiveComponent={isMobile ? MobileTable : undefined}
          mobileProps={{
            useFirstCell: true,
            fields: columns
              .filter(c => c.Header)
              .map(c => ({
                name: c.Header,
                accessor: c.accessor,
                Cell: c?.Cell
              })),
            handleEdit: (val: INews) => handleEdit(val),
            handleDelete: (val: INews) => handleDelete(val)
          }}
        />
        {!isLoadingNews && (
          <Pagination
            page={page}
            count={recordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        )}
      </Box>
      <AddEditNewsItemModal
        open={isShowingModal}
        onClose={() => {
          showModal(false);
          setCurrentNewsItem(null);
        }}
        currentNewsItem={currentNewsItem}
        setCurrentNewsItem={setCurrentNewsItem}
        fetchNewsItems={() => refetchNews()}
        setIsLoading={setIsLoading}
        isLoading={isLoading}
      />
    </>
  );
};
